import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import config from 'config';
import scaleSize from 'utils/scale-size';
import ScrollTopHack from 'components/ScrollTopHack';
import WithMouseControls from 'components/HOCs/WithMouseControls';
import { FocusableParent } from '../navigation';
import PageTitle from 'components/PageTitle';
import Button from '../Button';
import SpatialNavigationWrapper from '../navigation/SpatialNavigationWrapper';
import { compose } from 'redux';

const ROW_HEIGHT = scaleSize(422);

const GridWrapper = styled(FocusableParent)`
  height: ${scaleSize(1080)}px;
  position: relative;
`;

const GridListsWrapper = styled.div`
  height: ${scaleSize(1080)}px;
  padding-top: ${scaleSize(196)}px;
  transform: translate3d(
    0,
    -${({ focusedItemIndex, itemHeight, rowSize }) => scaleSize(itemHeight || ROW_HEIGHT) * Math.floor(focusedItemIndex / rowSize)}px,
    0
  );
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
`;

const GridEditButtonsWrapper = styled(FocusableParent)`
  position: absolute;
  top: 0;
  right: 0;
  text-align: right;
`;

class Grid extends Component {
  state = {
    focusedItemIndex: 0,
    focusedItemId: null,
    editMode: false,
  };

  componentDidUpdate(prevProps) {
    const { items } = this.props;
    const { items: prevItems } = prevProps;

    if (items.length < prevItems.length) {
      SpatialNavigationWrapper.focus();
    }
  }

  onFocus = (item, focusedItemIndex, itemId) => {
    const { onFocus } = this.props;
    onFocus && onFocus(item, focusedItemIndex);
    this.setState({ focusedItemIndex, itemId, focusedItemId: itemId });
  };

  onEnter = item => {
    const { onEditItem, onEnter } = this.props;

    if (this.state.editMode && onEditItem) return onEditItem(item);

    if (typeof onEnter === 'function') return onEnter(item);
  };

  enableEditMode = () => {
    this.setState({ editMode: true });
  };

  disableEditMode = () => {
    this.setState({ editMode: false }, () => {
      SpatialNavigationWrapper.focus();
    });
  };

  getRowIndex(index) {
    const { rowSize } = this.props;
    return Math.ceil((index + 1) / rowSize);
  }

  renderContents() {
    const { items, component, withoutContent, itemSize = {}, gridId } = this.props;
    const { editMode } = this.state;

    const GridItemComponent = component;

    if (!items || !items.length || !GridItemComponent) {
      return withoutContent || null;
    }

    return items.map((item, index) => {
      const itemId = `${gridId}-item-${item.id}`;
      return (
        <GridItemComponent
          item={item}
          key={itemId}
          id={itemId}
          index={index}
          onEnter={index => this.onEnter(items[index])}
          editMode={editMode}
          onFocus={index => this.onFocus(items[index], index, itemId)}
          itemSize={itemSize}
        />
      );
    });
  }

  renderEditButtons() {
    const { items /* onRemoveAll */ } = this.props;
    const { editMode } = this.state;
    const buttonTitle = 'Edit';

    if (!items || !items.length) {
      return null;
    }

    return (
      <GridEditButtonsWrapper sectionName={'EDIT_MODE'} id={'EDIT_MODE'}>
        {!editMode ? (
          <Button onEnter={this.enableEditMode} id={'edit-button'}>
            {buttonTitle}
          </Button>
        ) : (
          <>
            {/* <Button onEnter={onRemoveAll}>Очистить всё</Button> */}
            <Button onEnter={this.disableEditMode}>{'Finish'}</Button>
          </>
        )}
      </GridEditButtonsWrapper>
    );
  }

  render() {
    const { rowSize, itemSize, title, isEditable, gridId } = this.props;
    const { focusedItemIndex } = this.state;

    return (
      <GridWrapper className={this.props.className}>
        <GridListsWrapper
          focusedItemIndex={focusedItemIndex}
          rowSize={rowSize}
          itemHeight={itemSize.height + itemSize.metadataHeight}
        >
          <PageTitle>{title}</PageTitle>
          {isEditable && this.renderEditButtons()}
          <FocusableParent
            defaultSection
            sectionName={'GRID'}
            id={gridId}
            config={{
              enterTo: 'last-focused',
              leaveFor: {
                right: '',
                up: isEditable ? '@EDIT_MODE' : '',
                down: '',
              },
              debounce: {
                time: 200,
                options: {
                  leading: true,
                  trailing: false,
                  maxWait: 200,
                },
              },
            }}
          >
            {this.renderContents()}
          </FocusableParent>
        </GridListsWrapper>
      </GridWrapper>
    );
  }
}

Grid.defaultProps = {
  rowSize: 4,
  itemSize: {},
};

Grid.propTypes = {
  gridId: PropTypes.string.isRequired,
  rowSize: PropTypes.number,
};

export default compose(
  ScrollTopHack,
  WithMouseControls,
)(Grid);
